import { Form, Card, Alert, Button, Spinner, Badge } from 'react-bootstrap'
import { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'

interface UserDifyConfigTabProps {
  userId: number
  orgDifyEnabled: boolean
}

export const UserDifyConfigTab = ({ userId, orgDifyEnabled }: UserDifyConfigTabProps) => {
  const [usePersonal, setUsePersonal] = useState(false)
  const [difyApiKey, setDifyApiKey] = useState('')
  const [difyApiUrl, setDifyApiUrl] = useState('https://api.dify.ai/v1')
  const [difyAppId, setDifyAppId] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showApiKey, setShowApiKey] = useState(false)

  // Cargar configuración actual
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`/api/users/${userId}/dify-config/`)
        setUsePersonal(response.data.use_personal || false)
        setDifyApiKey(response.data.dify_api_key || '')
        setDifyApiUrl(response.data.dify_api_url || 'https://api.dify.ai/v1')
        setDifyAppId(response.data.dify_app_id || '')
      } catch (error) {
        console.warn('No Dify config found for user, using defaults')
      } finally {
        setLoading(false)
      }
    }

    loadConfig()
  }, [userId])

  const handleSave = async () => {
    if (usePersonal && !difyApiKey) {
      toast.warning('Por favor ingresa tu API Key')
      return
    }

    setSaving(true)
    try {
      await axios.post(`/api/users/${userId}/dify-config/`, {
        use_personal: usePersonal,
        dify_api_key: difyApiKey,
        dify_api_url: difyApiUrl,
        dify_app_id: difyAppId,
      })
      toast.success('Configuración de Dify guardada')
    } catch (error: any) {
      toast.error('Error al guardar configuración: ' + (error.response?.data?.error || error.message))
    } finally {
      setSaving(false)
    }
  }

  const handleTestConnection = async () => {
    if (!difyApiKey || !difyApiUrl) {
      toast.warning('Por favor completa API Key y URL')
      return
    }

    try {
      await axios.get(`${difyApiUrl}/parameters`, {
        headers: { Authorization: `Bearer ${difyApiKey}` },
        timeout: 5000,
      })
      toast.success('✅ Conexión a Dify verificada')
    } catch (error: any) {
      const errorMsg = error.response?.status === 401
        ? 'API Key inválida'
        : error.message || 'Error de conexión'
      toast.error(`❌ Error: ${errorMsg}`)
    }
  }

  if (loading) {
    return (
      <div className="pt-4">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div className="pt-4">
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">
            <span className="me-2">🤖</span>
            Configuración Personal de Dify
          </h5>
        </Card.Header>
        <Card.Body>
          {orgDifyEnabled ? (
            <>
              <Alert variant="info" className="mb-4">
                <small>
                  Tu organización tiene Dify habilitado. Por defecto usarás esa configuración.
                </small>
              </Alert>

              <Form.Group className="mb-4">
                <Form.Check
                  type="switch"
                  label="Usar mis propias credenciales de Dify"
                  checked={usePersonal}
                  onChange={(e) => setUsePersonal(e.target.checked)}
                  id="dify-personal-switch"
                />
                <Form.Text className="text-muted d-block mt-2">
                  Si activas esto, tus mensajes irán a tu propia cuenta de Dify.
                  Esto te permite usar tus propios modelos y configuraciones.
                </Form.Text>
              </Form.Group>

              {usePersonal && (
                <>
                  {/* API Key */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      API Key
                      <span className="text-danger ms-1">*</span>
                    </Form.Label>
                    <div className="input-group">
                      <Form.Control
                        type={showApiKey ? 'text' : 'password'}
                        value={difyApiKey}
                        onChange={(e) => setDifyApiKey(e.target.value)}
                        placeholder="app-xxxxx"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowApiKey(!showApiKey)}
                        title={showApiKey ? 'Ocultar' : 'Mostrar'}
                      >
                        {showApiKey ? '👁️‍🗨️' : '🔒'}
                      </Button>
                    </div>
                    <Form.Text className="text-muted d-block mt-2">
                      Obtén tu API Key en tu{' '}
                      <a href="https://cloud.dify.ai" target="_blank" rel="noopener noreferrer">
                        dashboard de Dify
                      </a>
                    </Form.Text>
                  </Form.Group>

                  {/* API URL */}
                  <Form.Group className="mb-3">
                    <Form.Label>API URL</Form.Label>
                    <Form.Control
                      type="url"
                      value={difyApiUrl}
                      onChange={(e) => setDifyApiUrl(e.target.value)}
                      placeholder="https://api.dify.ai/v1"
                    />
                    <Form.Text className="text-muted d-block mt-2">
                      Por defecto: https://api.dify.ai/v1
                    </Form.Text>
                  </Form.Group>

                  {/* App ID */}
                  <Form.Group className="mb-3">
                    <Form.Label>App ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={difyAppId}
                      onChange={(e) => setDifyAppId(e.target.value)}
                      placeholder="UUID de la aplicación en Dify"
                    />
                    <Form.Text className="text-muted d-block mt-2">
                      Requerido para enrutar los mensajes a la App correcta.
                    </Form.Text>
                  </Form.Group>

                  {/* Test Connection Button */}
                  <div className="mb-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleTestConnection}
                      disabled={!difyApiKey}
                      className="me-2"
                    >
                      💾 Probar conexión
                    </Button>
                  </div>

                  {/* Warning Box */}
                  <div className="bg-warning bg-opacity-10 p-3 rounded border-start border-warning border-4 mb-3">
                    <small className="text-dark d-block">
                      <strong>⚠️ Importante:</strong> Tu API Key se almacenará de forma segura.
                      Solo úsalo si confías en la plataforma.
                    </small>
                  </div>
                </>
              )}

              {/* Save Button */}
              {usePersonal && (
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Guardando...
                      </>
                    ) : (
                      '💾 Guardar cambios'
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setUsePersonal(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              )}

              {!usePersonal && (
                <div className="bg-light p-3 rounded border-start border-info border-4">
                  <small className="text-muted d-block">
                    <strong>💡 Nota:</strong> Actualmente usas la configuración de tu organización.
                  </small>
                </div>
              )}
            </>
          ) : (
            <Alert variant="warning">
              <small>
                ⚠️ Dify no está habilitado en tu organización. Contacta a tu administrador
                para habilitarlo.
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
